import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Markdown } from '../components/Markdown'
import { useKnowledge } from '../hooks/useKnowledge'
import type { FetchedNode, StructureNode } from '../api/knowledge'

function StructureTree({
  nodes,
  selectedId,
  onSelect,
}: {
  nodes: StructureNode[]
  selectedId: string | null
  onSelect: (nodeId: string) => void
}) {
  if (!nodes.length) return null
  return (
    <ul className="k-tree">
      {nodes.map((n) => (
        <li key={n.node_id}>
          <button
            type="button"
            className={`k-tree-row${selectedId === n.node_id ? ' is-on' : ''}`}
            onClick={() => onSelect(n.node_id)}
          >
            <span className="k-tree-id">{n.node_id}</span>
            <span className="k-tree-title">{n.title}</span>
          </button>
          {n.nodes && n.nodes.length > 0 ? (
            <StructureTree
              nodes={n.nodes}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/** Document detail — crumbs + structure browser, Todo detail density. */
export function KnowledgeDocPage() {
  const { docId = '' } = useParams()
  const { getDoc, getStructureCached, fetchNode, activate, archive } = useKnowledge()

  const doc = getDoc(docId)
  const [structure, setStructure] = useState<StructureNode[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [fetched, setFetched] = useState<FetchedNode[]>([])
  const [structureLoading, setStructureLoading] = useState(true)
  const [nodeBusy, setNodeBusy] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rawView, setRawView] = useState(false)

  const onSelectNode = useCallback(
    async (nodeId: string) => {
      setSelectedNodeId(nodeId)
      setNodeBusy(true)
      setError(null)
      try {
        setFetched(await fetchNode(docId, nodeId))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'fetch_failed')
      } finally {
        setNodeBusy(false)
      }
    },
    [docId, fetchNode],
  )

  useEffect(() => {
    let cancelled = false
    setStructure([])
    setSelectedNodeId(null)
    setFetched([])
    setError(null)
    setStructureLoading(true)
    getStructureCached(docId)
      .then((tree) => {
        if (cancelled) return
        setStructure(tree)
        const firstId = tree[0]?.node_id
        if (firstId) void onSelectNode(firstId)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'load_failed')
      })
      .finally(() => {
        if (!cancelled) setStructureLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [docId, getStructureCached, onSelectNode])

  const runActivate = async () => {
    setActionBusy(true)
    setError(null)
    try {
      await activate(docId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'activate_failed')
    } finally {
      setActionBusy(false)
    }
  }

  const runArchive = async () => {
    setActionBusy(true)
    setError(null)
    try {
      await archive(docId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'archive_failed')
    } finally {
      setActionBusy(false)
    }
  }

  if (!doc) {
    return (
      <div className="shell">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/knowledge" className="crumb">
            ← Knowledge
          </Link>
        </nav>
        <p className="empty">{error ? error : 'Document not found.'}</p>
      </div>
    )
  }

  return (
    <div className="shell">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/knowledge" className="crumb">
          ← Knowledge
        </Link>
        <span className="crumb-sep" aria-hidden>
          ←
        </span>
        <span className="crumb current" title={doc.title || doc.slug}>
          {doc.title || doc.slug}
        </span>
      </nav>

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      <header className="top compact">
        <div className="brand">
          <span className="mark">{doc.slug}</span>
          <h1>{doc.title || doc.slug}</h1>
        </div>
        {doc.description ? <p className="lede tight">{doc.description}</p> : null}
        <div className="todo-meta k-detail-meta">
          <span className={`pill status-pill status-${doc.status}`}>{doc.status}</span>
          <span className="pill muted">
            {doc.node_count} nodes · {doc.line_count} lines · v{doc.version}
          </span>
        </div>
        <div className="k-detail-actions">
          {doc.status === 'archived' ? (
            <button
              type="button"
              className="ghost"
              disabled={actionBusy}
              onClick={() => void runActivate()}
            >
              Restore
            </button>
          ) : (
            <>
              {doc.status !== 'active' ? (
                <button
                  type="button"
                  className="ghost"
                  disabled={actionBusy}
                  onClick={() => void runActivate()}
                >
                  Activate
                </button>
              ) : null}
              <button
                type="button"
                className="ghost"
                disabled={actionBusy}
                onClick={() => void runArchive()}
              >
                Archive
              </button>
            </>
          )}
        </div>
      </header>

      <section className="k-section">
        <h2 className="k-section-title">Structure</h2>
        {structureLoading ? (
          <p className="empty">Loading…</p>
        ) : structure.length === 0 ? (
          <p className="empty">Empty tree.</p>
        ) : (
          <StructureTree
            nodes={structure}
            selectedId={selectedNodeId}
            onSelect={(id) => void onSelectNode(id)}
          />
        )}
      </section>

      <section className="k-section">
        <div className="k-section-head">
          <h2 className="k-section-title">Node content</h2>
          {fetched.length > 0 ? (
            <div className="segs k-view-toggle" role="group" aria-label="View">
              <button
                type="button"
                className={rawView ? 'seg' : 'seg on'}
                onClick={() => setRawView(false)}
              >
                Rendered
              </button>
              <button
                type="button"
                className={rawView ? 'seg on' : 'seg'}
                onClick={() => setRawView(true)}
              >
                Raw
              </button>
            </div>
          ) : null}
        </div>
        {nodeBusy && !fetched.length ? <p className="empty">Fetching…</p> : null}
        {!selectedNodeId ? (
          <p className="empty">Select a node to fetch its body.</p>
        ) : null}
        {fetched.map((n) => (
          <article key={n.node_id} className="k-node-body">
            <header>
              <span className="k-tree-id">{n.node_id}</span>
              <strong>{n.title}</strong>
            </header>
            {n.summary ? <p className="lede tight">{n.summary}</p> : null}
            {rawView ? (
              <pre>{n.content || '(empty)'}</pre>
            ) : (
              <Markdown text={n.content || '*(empty)*'} />
            )}
          </article>
        ))}
      </section>
    </div>
  )
}
